// The drawing of the knowledge graph — hand-laid SVG, no layout library.
//
// The backend has already done the hard part: `layers` is a topological sort
// (plan §2.2), so every edge points strictly downward and there is nothing to
// simulate. What is left is placing boxes in rows and bending a curve between
// them, which is a hundred lines rather than a dependency — and a dependency
// here would also have to be made to work at 360 px (webapp.md §3), which
// force-directed layouts are bad at.
//
// The SVG scrolls inside its own container rather than scaling to fit: at a
// dozen-plus nodes a fit-to-width drawing puts the labels below legibility, and
// an unreadable picture is worth less than a scrollable one.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const BOX_W = 168;
const BOX_H = 46;
const COL_GAP = 24;
const ROW_GAP = 76;
const WRAP_GAP = 14; // between wrapped rows of ONE layer — peers, so tighter
const PAD = 28;

const COLOUR = { fill: "#0c4a6e", stroke: "#38bdf8", text: "#e0f2fe" }; // sky

// Below this the labels stop being readable, so the drawing scrolls instead of
// shrinking further. An unreadable picture is worth less than a scrollable one.
const MIN_SCALE = 0.78;

const MAX_CHARS = 23; // ~fits BOX_W at 11.5px in the UI sans stack
const MAX_LINES = 2;

/** Break a title across at most two lines, on word boundaries.
 *
 * A long title cut at one line loses its qualifying words, which are often
 * what tells two nodes apart. Two lines hold the whole name.
 */
function wrapTitle(title) {
  const lines = [];
  let line = "";
  for (const word of title.split(/\s+/)) {
    const next = line ? `${line} ${word}` : word;
    if (next.length <= MAX_CHARS) {
      line = next;
    } else {
      if (line) lines.push(line);
      line = word;
      if (lines.length === MAX_LINES) break;
    }
  }
  if (line && lines.length < MAX_LINES) lines.push(line);
  if (lines.length === MAX_LINES) {
    // Anything that did not fit is signalled on the last line, not dropped
    // silently — the tooltip has the whole title.
    const used = lines.join(" ");
    if (used.length < title.length) {
      lines[MAX_LINES - 1] = `${lines[MAX_LINES - 1].slice(0, MAX_CHARS - 1)}…`;
    }
  }
  return lines;
}

/** Order each layer so parents sit near their children — one barycentre pass. */
function orderLayers(layers, edges) {
  const parentsOf = new Map();
  edges.forEach(({ source, target }) => {
    if (!parentsOf.has(target)) parentsOf.set(target, []);
    parentsOf.get(target).push(source);
  });

  const placed = new Map(); // slug -> index within its layer
  return layers.map((layer, depth) => {
    let nodes = layer.nodes;
    if (depth > 0) {
      // A node with no placed parent keeps its alphabetical position; sorting is
      // stable, so those stay put instead of being flung to one end.
      nodes = [...nodes].sort((a, b) => {
        const bary = (slug, fallback) => {
          const parents = (parentsOf.get(slug) || [])
            .map((p) => placed.get(p))
            .filter((i) => i !== undefined);
          if (!parents.length) return fallback;
          return parents.reduce((s, i) => s + i, 0) / parents.length;
        };
        return bary(a.slug, 0) - bary(b.slug, 0);
      });
    }
    nodes.forEach((n, i) => placed.set(n.slug, i));
    return nodes;
  });
}

/**
 * @param onSelect  called with a slug when a node is clicked. Supply it when the
 *   graph is shown beside a content pane, so a click swaps the pane and the
 *   drawing stays where it is. Omit it and a click routes to the node's page.
 * @param activeSlug  marked with a brighter border. Marking only — it does not
 *   dim anything, so a page showing a whole sub-graph can point at one node
 *   inside it without greying out the rest.
 * @param focusSlug  dimmed around, exactly as hovering does. Pass it when the
 *   node is the page's subject and its neighbourhood is what you want read.
 */
export default function GraphCanvas({
  layers,
  edges,
  activeSlug = null,
  focusSlug = null,
  onSelect = null,
}) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);

  // Measured before the layout runs, because the layout depends on it: how many
  // boxes fit across decides how a layer wraps. The pane's width depends on the
  // viewport, on the two-column split, and on the browser's root font size (rem
  // again), so measuring is the only thing that survives all three.
  const boxRef = useRef(null);
  const [available, setAvailable] = useState(null);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    // clientWidth includes the box's own padding, and that padding is in rem
    // like everything else, so it is read rather than assumed.
    const measure = () => {
      const cs = window.getComputedStyle(el);
      const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      setAvailable(el.clientWidth - pad);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (typeof ResizeObserver !== "undefined") return undefined;
    const onResize = () => {
      const el = boxRef.current;
      if (!el) return;
      const cs = window.getComputedStyle(el);
      setAvailable(
        el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight),
      );
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { positions, width, height, ordered } = useMemo(() => {
    const ordered = orderLayers(layers, edges);
    const widest = Math.max(1, ...ordered.map((l) => l.length));

    // A layer wraps onto as many rows as it needs to fit the pane. Before edges
    // were hidden a layer had to be one straight row or the curves crossed it;
    // now that only one node's edges are ever drawn, wrapping costs nothing and
    // buys the thing that matters — every node on screen, because a node you
    // have to scroll to find is a node you will not click.
    const fits = available
      ? Math.floor((available - PAD * 2 + COL_GAP) / (BOX_W + COL_GAP))
      : widest;
    const cols = Math.max(1, Math.min(widest, fits));
    const width = cols * BOX_W + (cols - 1) * COL_GAP + PAD * 2;

    const positions = new Map();
    let y = PAD;
    ordered.forEach((nodes, depth) => {
      for (let start = 0; start < nodes.length; start += cols) {
        const row = nodes.slice(start, start + cols);
        const rowW = row.length * BOX_W + (row.length - 1) * COL_GAP;
        const left = (width - rowW) / 2;
        const rowY = y; // captured: `y` advances below, the closure must not see that
        row.forEach((node, i) => {
          positions.set(node.slug, {
            x: left + i * (BOX_W + COL_GAP),
            y: rowY,
            node,
          });
        });
        // Rows inside one layer are peers, so they sit close together; the wide
        // gap is reserved for a change of layer, where it means something.
        y += BOX_H + (start + cols < nodes.length ? WRAP_GAP : 0);
      }
      if (depth < ordered.length - 1) y += ROW_GAP;
    });

    return { positions, width, height: y + BOX_H / 2 + PAD, ordered };
  }, [layers, edges, available]);

  // Hover wins while the pointer is down on a node; otherwise the page's chosen
  // focus, if it named one. `activeSlug` deliberately does NOT dim: on the
  // reading-path page the whole closure IS the content, and dimming everything
  // not adjacent to the target would grey out the very thing the page shows.
  const focus = hovered || focusSlug;
  // Edges are drawn ONLY for the focused node. With every edge on screen at
  // once the picture is a hairball the moment the graph passes a couple of dozen
  // nodes — 55 edges over 29 nodes already needed more width than the pane had,
  // and no individual relationship was readable in it. Nothing is lost: the
  // layer a node sits in still shows its depth, and the edges that matter are
  // one hover away.
  const shownEdges = useMemo(
    () =>
      focus ? edges.filter(({ source, target }) => source === focus || target === focus) : [],
    [focus, edges],
  );

  const lit = useMemo(() => {
    if (!focus) return null;
    const set = new Set([focus]);
    shownEdges.forEach(({ source, target }) => {
      set.add(source);
      set.add(target);
    });
    return set;
  }, [focus, shownEdges]);

  if (!ordered.length) return null;

  // Shrink to fit, down to the point where the labels would stop being legible;
  // past that the panel scrolls, which is what happens on a phone.
  const scale =
    available && available < width ? Math.max(MIN_SCALE, available / width) : 1;
  const renderW = Math.round(width * scale);
  const renderH = Math.round(height * scale);

  return (
    <div
      ref={boxRef}
      className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/60 p-2"
    >
      <svg
        width={renderW}
        height={renderH}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Knowledge graph: dependencies point downward"
        style={{ minWidth: renderW }}
      >
        <defs>
          <marker
            id="kg-arrow-lit"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0,0 L8,4 L0,8 z" fill="#38bdf8" />
          </marker>
        </defs>

        {/* Edges first, so a box always covers the line that meets it. Only the
            focused node's edges are drawn at all — see `shownEdges`. */}
        {shownEdges.map(({ source, target }) => {
          const a = positions.get(source);
          const b = positions.get(target);
          if (!a || !b) return null;
          const x1 = a.x + BOX_W / 2;
          const y1 = a.y + BOX_H;
          const x2 = b.x + BOX_W / 2;
          const y2 = b.y;
          const bend = Math.min(44, (y2 - y1) / 2);
          return (
            <path
              key={`${source}->${target}`}
              d={`M ${x1} ${y1} C ${x1} ${y1 + bend}, ${x2} ${y2 - bend}, ${x2} ${y2 - 4}`}
              fill="none"
              stroke="#38bdf8"
              strokeWidth={2}
              markerEnd="url(#kg-arrow-lit)"
            />
          );
        })}

        {[...positions.values()].map(({ x, y, node }) => {
          const c = COLOUR;
          const dim = lit && !lit.has(node.slug);
          const isActive = node.slug === activeSlug;
          return (
            <g
              key={node.slug}
              transform={`translate(${x}, ${y})`}
              onMouseEnter={() => setHovered(node.slug)}
              onMouseLeave={() => setHovered(null)}
              onClick={() =>
                onSelect ? onSelect(node.slug) : navigate(`/graph/node/${node.slug}`)
              }
              style={{ cursor: "pointer" }}
              opacity={dim ? 0.3 : 1}
            >
              <title>
                {node.title}
              </title>
              <rect
                width={BOX_W}
                height={BOX_H}
                rx="8"
                fill={c.fill}
                stroke={isActive ? "#e2e8f0" : c.stroke}
                strokeWidth={isActive ? 2.5 : 1.25}
              />
              {(() => {
                const lines = wrapTitle(node.title);
                // Centre the block: one line sits on the middle, two straddle it.
                const top = BOX_H / 2 + 4 - (lines.length - 1) * 7;
                return (
                  <text
                    x={BOX_W / 2}
                    textAnchor="middle"
                    fontSize="11.5"
                    fill={c.text}
                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                  >
                    {lines.map((line, i) => (
                      <tspan key={line + i} x={BOX_W / 2} y={top + i * 14}>
                        {line}
                      </tspan>
                    ))}
                  </text>
                );
              })()}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
